import React, { useState, useEffect } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { Tooltip } from "react-tooltip";
import api from "../../api";
import "react-tooltip/dist/react-tooltip.css";
import ChartInfoPopup from "./ChartInfoPopup";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const UserPresenceMap = () => {
  const [markers, setMarkers] = useState([]);
  const [tooltipContent, setTooltipContent] = useState("");

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchPresence = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/auth/user-presence`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const users = response.data.users || [];
        const countryMap = new Map();

        users.forEach((user) => {
          user.active_sessions.forEach((session) => {
            if (session.latitude && session.longitude && session.country) {
              const key = session.country;

              if (!countryMap.has(key)) {
                countryMap.set(key, {
                  country: session.country,
                  coordinates: [session.longitude, session.latitude],
                  count: 0,
                  visitors: new Set(),
                });
              }

              const entry = countryMap.get(key);
              entry.count += 1;
              entry.visitors.add(user.name);
            }
          });
        });

        // Convert Map → Array
        const aggregatedMarkers = Array.from(countryMap.values()).map(
          (entry) => ({
            ...entry,
            visitors: Array.from(entry.visitors),
          })
        );

        setMarkers(aggregatedMarkers);
      } catch (err) {
        console.error("Failed to fetch user presence:", err);
      }
    };

    fetchPresence();
  }, [token]);

  return (
    <div style={{ borderRadius: "12px", padding: "5px",  }}>
      {/* <div>
        <span className="border-start border-4 border-dark"></span>
        <span className="ps-2 refresh-prompts fw-bold">User Presence</span>
      </div> */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <h4 className="fw-bold mb-0">
          <span className="border-start border-4 border-dark ps-2 refresh-prompts">
            User Presence
          </span>
        </h4>

        <div className="chart-info-anchor">
          <ChartInfoPopup text="This world map illustrates where users are interacting with the chatbot, highlighting countries based on activity levels, with color intensity or marker size reflecting the number of users from each region. It helps you identify global usage patterns, key markets, and geographic growth areas." />
        </div>
      </div>

      <div style={{ height: "400px" }}>
        <ComposableMap
          projectionConfig={{ scale: 160 }}
          width={800}
          height={500}
          style={{ width: "100%", height: "100%", outline: "none" }}
          tabIndex={-1} // prevent focus outline
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => {
                    const { NAME, ISO_A2 } = geo.properties;
                    setTooltipContent(`${NAME} (${ISO_A2})`);
                  }}
                  onMouseLeave={() => {
                    setTooltipContent("");
                  }}
                  style={{
                    default: {
                      fill: "#ECEFF1",
                      stroke: "#607D8B",
                      strokeWidth: 0.5,
                    },
                    hover: {
                      fill: "#CFD8DC",
                      stroke: "#607D8B",
                      strokeWidth: 1,
                    },
                    pressed: { fill: "#B0BEC5" },
                  }}
                />
              ))
            }
          </Geographies>

          {markers.map(({ country, coordinates, count, visitors }) => (
            <Marker
              key={country}
              coordinates={coordinates}
              data-tooltip-id="user-tooltip"
              data-tooltip-content={`${country} - ${count} visitor(s)`}
            >
              <circle r={6} fill="#FF5722" stroke="#fff" strokeWidth={1.5} />
            </Marker>
          ))}
        </ComposableMap>

        <Tooltip id="user-tooltip">{tooltipContent}</Tooltip>
      </div>
    </div>
  );
};

export default UserPresenceMap;
